import React, {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'antd'
import { ExclamationCircleOutlined, LockOutlined } from '@ant-design/icons'

import type { LinkedAccountItem } from '../../types/linkedAccount'
import { listLinkedAccounts, listMyLinkedAccounts } from '../../apis/linkedAccountApis'
import { isForbiddenError } from '../../util/linkedAccount/linkedAccountErrors'
import { getPermission, isHasPermission } from '../../redux/reducers/permission'
import { PERMISSION } from '../../constants/permission'
import LinkedAccountList, { LinkedAccountListHeader } from './LinkedAccountList'
import AddLinkedAccountModal from './AddLinkedAccountModal'
import EditLinkedAccountModal from './EditLinkedAccountModal'
import RevealPasswordModal from './RevealPasswordModal'
import DeleteLinkedAccountModal from './DeleteLinkedAccountModal'

/**
 * Container "Tài khoản liên kết" (SP-005, Task 7) — tải danh sách, tính `canManage`, giữ state
 * của 4 hộp thoại (Thêm/Sửa/Nhận mật khẩu/Xoá) và ghép `LinkedAccountList` (Task 3) với chúng.
 *
 * Task 8 (chưa viết) sẽ mount component này vào 4 drawer tài khoản đang có sẵn — nó KHÔNG tự biết
 * gì về drawer, chỉ nhận ba prop mô tả ngữ cảnh (`userId`/`isOwnView`/`active`).
 *
 * 🔴 Luật quyền — dòng quan trọng nhất của cả task này:
 * `canManage = isHasPermission(PERMISSION.MANAGE_LINKED_ACCOUNT, permissionRole) && !isOwnView`.
 * `isOwnView` LUÔN THẮNG (`FR-W-15`) — màn "Tài khoản của tôi" chỉ đọc với MỌI role, kể cả
 * `super_admin`/`managing_admin` đang xem CHÍNH bản ghi của họ. Không đảo thứ tự `&&` (không được
 * viết `!isOwnView && isHasPermission(...)` rồi lỡ tay short-circuit sai — giữ đúng thứ tự đã chốt
 * để một reviewer đọc qua là thấy ngay `isOwnView` là điều kiện phủ quyết).
 *
 * `permissionRole` đọc qua `useSelector(getPermission)` — ĐÚNG cách các nơi gọi `isHasPermission`
 * phổ biến nhất trong repo đang làm (`src/components/defaultLayout/header.js`,
 * `src/components/simPage/buttonChangeStatus.js`…), không phải cách `navItem.js` đọc thẳng
 * `localStorage` qua `handlePermission`. `navItem.js` chỉ được brief trỏ tới để xem CÁCH GỌI
 * `isHasPermission(PERMISSION.X, permissionRole)` (tham số thứ hai là gì), không phải để chép
 * nguyên đường lấy `permissionRole`.
 *
 * Card đã tự quyết định nút theo `canManage` (Task 3) — container chỉ cần truyền đúng cờ:
 * `canManage=true` ⇒ card chỉ có menu `…` (Chỉnh sửa/Xoá); `canManage=false` ⇒ card chỉ có
 * `Nhận mật khẩu`. Vì vậy trên màn vận hành, đường "Nhận mật khẩu" đi qua Sửa → nút `Xem mật khẩu`
 * (đã nối sẵn bên trong `EditLinkedAccountModal`, Task 6) — container chỉ cần mở đúng
 * `EditLinkedAccountModal`. Trên màn tự phục vụ, đường đó đi thẳng từ nút trên thẻ.
 *
 * 🔴 Tải danh sách khi `active` là `true` VÀ danh tính (`userId`, hoặc sentinel "của tôi") đang xem
 * KHÁC danh tính đã tải lần gần nhất (`loadedIdentityRef`) — chuyển tab qua lại KHÔNG gọi lại API,
 * nhưng đổi danh tính TRÊN CÙNG INSTANCE thì phải. Bắt buộc tự chống ở đây, không dựa vào việc nơi
 * gọi remount/đổi `key`: 5 drawer chi tiết đơn của cụm này (xem CLAUDE.md, mục "Drawer chi tiết đơn
 * kích hoạt") KHÔNG unmount khi đóng, chỉ đổi prop — Task 8 mount container này vào đúng 4 drawer
 * kiểu đó. Không tự chống thì "đóng drawer tài khoản A, mở drawer tài khoản B, mở tab" sẽ hiện
 * NGUYÊN mật khẩu/username của A dưới tên B, không lỗi, không cảnh báo — đúng thứ tệ nhất một màn
 * "tài khoản liên kết" có thể làm sai. Đổi danh tính còn XOÁ `items`/`errorKind` NGAY (đồng bộ,
 * trước khi lời gọi mới kịp trả lời) để không có khung hình nào lộ dữ liệu của danh tính cũ qua lớp
 * mờ `Spin` trong lúc chờ — không đợi tới khi `load()` tự ghi đè bằng kết quả mới.
 * Mọi thao tác GHI (tạo/sửa/xoá) và mọi `onRecordGone` (bản ghi đang thao tác đã bị xoá ở nơi khác)
 * đều tự gọi `load()` riêng cho ĐÚNG danh tính hiện tại, không đi qua effect này.
 *
 * DOM hook cho bộ test / Task 8:
 * - `.linked-account-tab` — gốc khi danh sách tải được (bọc `LinkedAccountList` + 4 hộp thoại).
 * - `.linked-account-tab__error` / `__error-text` / `__retry-btn` — trạng thái lỗi tải danh sách
 *   (thay THẾ `.linked-account-tab`, không render cả hai cùng lúc).
 */

export interface LinkedAccountTabProps {
  /**
   * Tài khoản ERP đang mở. Bỏ trống ⇒ dùng endpoint "của tôi".
   * Đối tác truyền `representative_id`, KHÔNG phải id bản ghi đối tác.
   */
  userId?: string
  /** true khi drawer mở ở màn "Tài khoản của tôi" (`isProfile` của các drawer). */
  isOwnView: boolean
  /** Tab đang được mở — chỉ gọi API khi true, tránh tải lúc drawer còn đóng. */
  active: boolean
}

type LoadErrorKind = 'forbidden' | 'generic'

/** Sentinel danh tính cho màn "của tôi" (`userId === undefined`) — `undefined` không dùng trực tiếp
 * làm giá trị `ref` được vì nó trùng với "chưa từng tải lần nào", không phân biệt được hai ca. */
const SELF_VIEW_IDENTITY = '__self__'
const identityOf = (userId: string | undefined): string => userId ?? SELF_VIEW_IDENTITY

/** Mảng rỗng hằng (module-level) — không dựng mảng mới mỗi lượt render khi đang chờ lượt tải đầu. */
const NO_ITEMS: readonly LinkedAccountItem[] = []

const FORBIDDEN_LOAD_MESSAGE = 'Bạn không có quyền xem tài khoản liên kết của tài khoản này.'
const GENERIC_LOAD_ERROR_MESSAGE = 'Không tải được danh sách tài khoản liên kết.'
const RETRY_LABEL = 'Thử lại'

/**
 * `permission.js`/reducer là JS không có kiểu — khai hẹp đúng phần đang dùng rồi ép qua `unknown`,
 * cùng khuôn `util/topupWizard/topupOrderListAccess.ts`. KHÔNG dùng `any`.
 */
const permissionValues = PERMISSION as unknown as Record<string, string>
const hasPermission = isHasPermission as unknown as (required: string, userPermission: unknown) => boolean
const selectPermissionRole = getPermission as unknown as (state: unknown) => unknown

const LinkedAccountTab = ({ userId, isOwnView, active }: LinkedAccountTabProps): JSX.Element => {
  const permissionRole = useSelector(selectPermissionRole)
  // 🔴 `isOwnView` LUÔN THẮNG — xem lời giải thích ở JSDoc đầu file, đừng đảo vế `&&`.
  const canManage = hasPermission(permissionValues.MANAGE_LINKED_ACCOUNT, permissionRole) && !isOwnView

  const [items, setItems] = useState<LinkedAccountItem[]>([])
  const [loading, setLoading] = useState(false)
  const [errorKind, setErrorKind] = useState<LoadErrorKind | null>(null)

  const [addOpen, setAddOpen] = useState(false)
  const [editItem, setEditItem] = useState<LinkedAccountItem | null>(null)
  const [revealItem, setRevealItem] = useState<LinkedAccountItem | null>(null)
  const [deleteItem, setDeleteItem] = useState<LinkedAccountItem | null>(null)

  /** Danh tính (`identityOf(userId)`) đã tải lần gần nhất trên INSTANCE này. `null` = chưa tải lần nào. */
  const loadedIdentityRef = useRef<string | null>(null)

  /**
   * Bản STATE của `loadedIdentityRef` — để lượt render ĐẦU TIÊN của một danh tính (trước khi effect tải kịp chạy) biết
   * mình "chưa tải" mà hiện vùng đang tải. Thiếu nó, lượt render đó có `loading=false` + danh sách rỗng ⇒ khối "chưa
   * được cấp tài khoản liên kết nào" NHÁY lên một khung hình rồi mới tới spinner (thấy trên Chrome dev 19/09/2026).
   */
  const [loadedIdentity, setLoadedIdentity] = useState<string | null>(null)
  // KHÔNG phụ thuộc `active`: chưa có kết quả cho danh tính này thì KHÔNG BAO GIỜ vẽ khối rỗng, kể cả khi pane đang ẩn.
  const awaitingFirstLoad = loadedIdentity !== identityOf(userId)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setErrorKind(null)
    try {
      const result = userId !== undefined ? await listLinkedAccounts(userId) : await listMyLinkedAccounts()
      setItems(result)
    } catch (error) {
      setItems([])
      setErrorKind(isForbiddenError(error) ? 'forbidden' : 'generic')
    } finally {
      setLoading(false)
    }
  }, [userId])

  /**
   * `load()` fire-and-forget thật (không ai `await`) — `.catch` chỉ log, không đổi state, đúng
   * khuôn có sẵn của repo (`util/topupWizard/useTopupQuote.ts`, tránh unhandled promise rejection
   * từ một lỗi ngoài dự kiến; bản thân `load()` đã tự bắt lỗi API/mạng bằng `try/catch` riêng).
   */
  const runLoad = useCallback((): void => {
    load().catch((error: unknown) => {
      console.error('LinkedAccountTab: tải danh sách tài khoản liên kết thất bại ngoài dự kiến', error)
    })
  }, [load])

  useEffect(() => {
    if (!active) return
    const identity = identityOf(userId)
    if (loadedIdentityRef.current === identity) return
    loadedIdentityRef.current = identity
    setLoadedIdentity(identity)
    // Đổi danh tính trên CÙNG instance (xem JSDoc đầu file) ⇒ xoá NGAY, đừng đợi `load()` — nếu
    // không, khung hình giữa lúc bấm mở và lúc lời gọi mới trả lời sẽ còn hiện đúng dữ liệu của
    // danh tính TRƯỚC, chỉ mờ đi qua `Spin`, không phải trống.
    setItems([])
    setErrorKind(null)
    runLoad()
  }, [active, userId, runLoad])

  /** Gọi lại sau MỌI thao tác ghi (`onCreated`/`onSaved`/`onDeleted`) và MỌI `onRecordGone`. */
  const handleWritten = (): void => { runLoad() }

  // 🔴 Phòng hờ tầng thứ hai (defence in depth): `LinkedAccountList`/`LinkedAccountCard` đã tự gác
  // ba nút này bằng `canManage`, nhưng container mang rủi ro quyền của cả tính năng nên KHÔNG được
  // tin tưởng mù quáng vào tầng con — một lỗi tương lai ở card (vd quên gác một nút mới) không
  // được phép mở được hộp thoại GHI trên màn chỉ-đọc. `handleRevealPassword` CỐ Ý không gác theo
  // `canManage`: nó là đường "Nhận mật khẩu" của màn tự phục vụ, chỉ dùng đúng lúc `canManage===false`.
  const handleAdd = (): void => {
    if (!canManage) return
    setAddOpen(true)
  }
  const handleCloseAdd = (): void => setAddOpen(false)

  const handleEdit = (target: LinkedAccountItem): void => {
    if (!canManage) return
    setEditItem(target)
  }
  const handleCloseEdit = (): void => setEditItem(null)

  const handleRevealPassword = (target: LinkedAccountItem): void => setRevealItem(target)
  const handleCloseReveal = (): void => setRevealItem(null)

  const handleDelete = (target: LinkedAccountItem): void => {
    if (!canManage) return
    setDeleteItem(target)
  }
  const handleCloseDelete = (): void => setDeleteItem(null)

  // Figma không vẽ trạng thái tải lỗi ⇒ dựng cùng khuôn khối rỗng (hàng tiêu đề + khối xám, vòng tròn
  // trắng chứa icon, câu 14px) để không lạc tông với phần còn lại. Không có nút Thêm: danh sách chưa
  // biết (lỗi chung) hoặc không có quyền (403).
  // Đang chờ lượt tải đầu của danh tính hiện tại ⇒ bỏ qua lỗi/dữ liệu còn sót của danh tính TRƯỚC (drawer không unmount).
  if (errorKind !== null && !awaitingFirstLoad) {
    return (
      <div className="linked-account-tab">
        <div className="linked-account-list">
          <LinkedAccountListHeader />
          <div className="linked-account-tab__error">
            <span className="linked-account-list__empty-icon" aria-hidden>
              {errorKind === 'forbidden' ? <LockOutlined /> : <ExclamationCircleOutlined />}
            </span>
            <p className="linked-account-tab__error-text">
              {errorKind === 'forbidden' ? FORBIDDEN_LOAD_MESSAGE : GENERIC_LOAD_ERROR_MESSAGE}
            </p>
            {errorKind === 'generic' && (
              <Button size="large" className="linked-account-tab__retry-btn" onClick={handleWritten}>
                {RETRY_LABEL}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="linked-account-tab">
      <LinkedAccountList
        items={awaitingFirstLoad ? NO_ITEMS : items}
        loading={loading || awaitingFirstLoad}
        canManage={canManage}
        isOwnView={isOwnView}
        onAdd={handleAdd}
        onRevealPassword={handleRevealPassword}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Chỉ Add cần `userId` trực tiếp (tạo bản ghi MỚI cho một tài khoản cụ thể). Nút "Thêm tài
          khoản" chỉ hiện khi `canManage` (⇒ `!isOwnView`), và màn quản lý luôn đi kèm `userId` —
          guard này chỉ là hàng rào thứ hai, phòng khi nơi gọi lỡ truyền tổ hợp không hợp lệ. */}
      {userId !== undefined && (
        <AddLinkedAccountModal
          open={addOpen}
          userId={userId}
          onCancel={handleCloseAdd}
          onCreated={handleWritten}
        />
      )}

      <EditLinkedAccountModal
        item={editItem}
        onCancel={handleCloseEdit}
        onSaved={handleWritten}
        onRecordGone={handleWritten}
      />

      <RevealPasswordModal
        item={revealItem}
        onClose={handleCloseReveal}
        onRecordGone={handleWritten}
      />

      <DeleteLinkedAccountModal
        item={deleteItem}
        onCancel={handleCloseDelete}
        onDeleted={handleWritten}
      />
    </div>
  )
}

export default LinkedAccountTab
